import { useParams, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Heart, ArrowRight, Sparkles, Layers } from 'lucide-react'
import { useProjects, useCategory } from '@/hooks/useProjects'
import { Skeleton } from '@/components/ui/skeleton'
import { ImageWithFallback } from '@/components/figma/ImageWithFallback'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'
import { PageLayout } from '@/components/PageLayout'
import { Meta } from '@/components/MetaManager'
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData'

function ProjectListSkeleton() {
  return (
    <PageLayout>
      <div className="min-h-screen bg-black">
        <div className="container mx-auto px-6 pt-32 pb-20">
          <Skeleton className="h-12 w-64 mb-4 mx-auto bg-white/10" />
          <Skeleton className="h-6 w-96 mb-12 mx-auto bg-white/10" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[400px] rounded-2xl bg-white/10" />
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default function ProjectList() {
  const { category } = useParams({ from: '/$category' }) as { category?: string }
  const { getLocalizedPath } = useLocalizedPath()
  
  const { data: projects, isLoading: projectsLoading } = useProjects(category)
  const { data: categoryData, isLoading: categoryLoading } = useCategory(category || '')
  const { t, i18n } = useTranslation()

  const statusStyles = {
    completed: {
      bg: 'bg-emerald-500/20',
      text: 'text-emerald-400',
      label: t.status.completed
    },
    'in-progress': {
      bg: 'bg-blue-500/20',
      text: 'text-blue-400',
      label: t.status.inProgress
    },
    planned: {
      bg: 'bg-purple-500/20',
      text: 'text-purple-400',
      label: t.status.planned
    }
  }

  const isTraumaCategory = category === 'trauma'

  if (projectsLoading || categoryLoading) {
    return <ProjectListSkeleton />
  }

  if (!categoryData) {
    return (
      <PageLayout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="glass-effect p-8 rounded-2xl text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Category Not Found</h1>
            <Link to={getLocalizedPath('/')} className="text-accent-blue hover:underline">
              {i18n.language === 'zh' ? '返回首页' : 'Go Home'}
            </Link>
          </div>
        </div>
      </PageLayout>
    )
  }

  const lang = i18n.language as 'en' | 'zh';
  const breadcrumbs = generateBreadcrumbs([
    { label: categoryData.title, path: `/${category}` }
  ], lang);

  return (
    <PageLayout>
      <Meta
        title={`${categoryData.title} | borninsea`}
        description={categoryData.description}
        keywords={i18n.language === 'zh' ? `${categoryData.title},项目,borninsea` : `${categoryData.title},projects,borninsea`}
      />
      <StructuredData type="website" breadcrumbs={breadcrumbs} />
      {/* Hero Section */}
      <div className="relative min-h-[50vh] bg-black overflow-hidden">
        {/* 背景渐变 */}
        <div className={`absolute inset-0 bg-gradient-to-br ${
          isTraumaCategory 
            ? 'from-rose-500/15 via-transparent to-pink-500/10' 
            : 'from-accent-blue/15 via-transparent to-accent-purple/10'
        }`} />
        
        {/* 装饰性光效 */}
        <div className={`absolute top-1/3 left-1/4 w-80 h-80 ${
          isTraumaCategory ? 'bg-rose-500/10' : 'bg-accent-blue/10'
        } rounded-full blur-3xl animate-pulse`} />
        <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-accent-purple/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* 内容 */}
        <div className="relative z-10 container mx-auto px-6 pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 glass-effect px-4 py-2 rounded-full mb-6">
              {isTraumaCategory ? (
                <Heart className="w-4 h-4 text-rose-400" />
              ) : (
                <Layers className="w-4 h-4 text-accent-blue" />
              )}
              <span className="text-sm font-medium text-white/80">{categoryData.title}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white leading-tight">
              {categoryData.title}
            </h1>
            
            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto whitespace-pre-line">
              {category === 'launch100'
                ? t.collections.challenge100.narrative.intro.body
                : category === 'grow100'
                ? t.collections.challenge100.narrative.grow.body
                : categoryData.description}
            </p>
            
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="glass-effect px-4 py-2 rounded-lg">
                <span className="text-2xl font-bold text-white">{projects?.length || 0}</span>
                <span className="text-white/60 ml-2 text-sm">{t.nav.projects}</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Trauma Category Intro */}
      {isTraumaCategory && (
        <div className="bg-background py-8">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="p-8 rounded-2xl bg-rose-500/5 border border-rose-500/20">
                <Heart className="w-10 h-10 text-rose-400 mx-auto mb-4" />
                <p className="text-foreground/80 leading-relaxed mb-6 text-center">
                  {t.categories.traumaIntro || '这里是我将四十年创伤探索经历产品化的成果。每一个产品都源于真实的痛苦与觉醒，希望能为你点亮一盏灯。'}
                </p>
                <div className="text-center">
                  <Link 
                    to={getLocalizedPath('/about/trauma')}
                    className="inline-flex items-center gap-2 text-rose-400 hover:text-rose-300 transition-colors"
                  >
                    {t.categories.traumaLearnMore || '了解我的完整故事'}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      <div className="bg-background py-16">
        <div className="container mx-auto px-6">
          {projects && projects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => {
                const status = statusStyles[project.status as keyof typeof statusStyles] || statusStyles.planned
                const tags = Array.isArray(project.tags) ? project.tags : []
                
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={getLocalizedPath(`/${category}/${project.id}`)}
                      className="group block bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      {/* Image */}
                      <div className="relative aspect-video overflow-hidden">
                        <ImageWithFallback
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute top-4 right-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
                            {status.label}
                          </span>
                        </div>
                        {project.is_featured && (
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-purple/20 text-accent-purple flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {t.common.featured || '精选'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
                          {project.title}
                        </h3>
                        {project.subtitle && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                            {project.subtitle}
                          </p>
                        )}
                        <p className="text-muted-foreground line-clamp-2 mb-4">
                          {project.description}
                        </p>
                        
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {tags.slice(0, 3).map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                            {tags.length > 3 && (
                              <span className="px-2 py-1 text-xs text-muted-foreground">
                                +{tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="glass-effect inline-block p-8 rounded-2xl">
                <Sparkles className="w-8 h-8 text-accent-purple mx-auto mb-4" />
                <p className="text-muted-foreground">{t.common.noProjects || '暂无项目'}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PageLayout>
  )
}
